// frontend/src/lib/entraAuth.js
// Cliente centralizado para Microsoft Entra ID con MSAL.

import { PublicClientApplication } from '@azure/msal-browser';

const readEnv = (names, fallback = '') => {
  for (const name of names) {
    const value = process.env[name];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return fallback;
};

const entraClientId = readEnv(['REACT_APP_ENTRA_CLIENT_ID', 'NEXT_PUBLIC_ENTRA_CLIENT_ID']);
const entraAuthority = readEnv(['REACT_APP_ENTRA_AUTHORITY', 'NEXT_PUBLIC_ENTRA_AUTHORITY']);
const entraRedirectUri = readEnv(
  ['REACT_APP_ENTRA_REDIRECT_URI', 'NEXT_PUBLIC_ENTRA_REDIRECT_URI'],
  'http://localhost:3030'
);
const entraApiScope = readEnv(['REACT_APP_ENTRA_API_SCOPE', 'NEXT_PUBLIC_ENTRA_API_SCOPE']);
const bootstrapAdminUpns = readEnv(
  ['REACT_APP_BOOTSTRAP_ADMIN_UPNS', 'NEXT_PUBLIC_BOOTSTRAP_ADMIN_UPNS'],
  ''
);

const baseScopes = ['openid', 'profile', 'email'];
const requestScopes = entraApiScope
  ? Array.from(new Set([...baseScopes, entraApiScope]))
  : baseScopes;

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: entraClientId,
    authority: entraAuthority,
    redirectUri: entraRedirectUri,
    postLogoutRedirectUri: entraRedirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
});

let msalInitPromise = null;
let msalRedirectHandledPromise = null;

const ensureMsalInitialized = async () => {
  if (!msalInitPromise) {
    msalInitPromise = msalInstance.initialize();
  }

  await msalInitPromise;

  if (!msalRedirectHandledPromise) {
    msalRedirectHandledPromise = msalInstance.handleRedirectPromise().then((response) => {
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      }

      return response;
    });
  }

  await msalRedirectHandledPromise;
};

const ensureActiveAccount = () => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
  if (account) {
    msalInstance.setActiveAccount(account);
  }
  return account;
};

const loginRequest = {
  scopes: requestScopes,
  prompt: 'select_account',
};

export const getEntraBootstrapAdminUpns = () => bootstrapAdminUpns;

export const getEntraSession = async () => {
  await ensureMsalInitialized();
  const account = ensureActiveAccount();
  if (!account) {
    return null;
  }

  const result = await msalInstance.acquireTokenSilent({
    account,
    scopes: requestScopes,
  });

  return {
    account,
    accessToken: result.accessToken || null,
    idToken: result.idToken || null,
    token: result.idToken || result.accessToken || null,
  };
};

export const loginWithEntra = async () => {
  await ensureMsalInitialized();

  if (!entraClientId || !entraAuthority || requestScopes.length === 0) {
    throw new Error('La configuración de Microsoft Entra ID está incompleta.');
  }

  const result = await msalInstance.loginPopup(loginRequest);
  const account = result.account || null;

  if (account) {
    msalInstance.setActiveAccount(account);
  }

  if (!account) {
    throw new Error('No se pudo completar la autenticación con Microsoft Entra ID.');
  }

  const tokenResult = await msalInstance.acquireTokenSilent({
    account,
    scopes: requestScopes,
  });

  return {
    account,
    accessToken: tokenResult.accessToken || result.accessToken || null,
    idToken: tokenResult.idToken || result.idToken || null,
    token: tokenResult.idToken || result.idToken || tokenResult.accessToken || result.accessToken || null,
  };
};

export const logoutFromEntra = async () => {
  await ensureMsalInitialized();

  const account = ensureActiveAccount();

  if (!account) {
    return;
  }

  await msalInstance.logoutPopup({
    account,
    postLogoutRedirectUri: entraRedirectUri,
  });
};

export const getEntraConfig = () => ({
  clientId: entraClientId,
  authority: entraAuthority,
  redirectUri: entraRedirectUri,
  apiScope: entraApiScope,
  requestScopes,
});

export { msalInstance };